// Valores dos parâmetros dos modelos de orçamento ({{nome}}, {{produtos}}...), usados pela
// tela de Contratos e pelo orçamento rápido da conversa.
import { quoteInvoiceItems } from './quoteInvoiceItems';
import {
  normalizeRentalDays,
  itemRentalDays,
  skipsRentalDays,
} from './rentalDays';

const formatCurrency = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value || 0));

const formatDateTime = value =>
  value
    ? new Date(value).toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : '-';

const escapeHtml = value =>
  String(value ?? '').replace(
    /[&<>"']/g,
    character =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[character]
  );

const quoteItemQuantity = item => {
  const quantity = Number(item?.quantity || 0);
  return quantity > 0 ? quantity : 1;
};

const quoteItemTotal = item =>
  Number(item?.total || 0) ||
  quoteItemQuantity(item) * Number(item?.unit_price || item?.unitPrice || 0);

const quoteTotal = payload =>
  quoteInvoiceItems(payload).reduce(
    (total, item) => total + quoteItemTotal(item),
    0
  );

const quoteProductSummary = payload =>
  quoteInvoiceItems(payload)
    .map(item => `${quoteItemQuantity(item)}x ${item.product_name || 'Item'}`)
    .join(', ');

const quoteProductList = payload =>
  quoteInvoiceItems(payload)
    .map(item => {
      const quantity = quoteItemQuantity(item);
      const unitPrice =
        Number(item.unit_price ?? item.unitPrice ?? 0) ||
        quoteItemTotal(item) / quantity;
      const days = itemRentalDays(item, payload?.rental_days);
      const daysSuffix = days > 1 ? ` × ${days} diárias` : '';
      return `${escapeHtml(item.product_name || 'Item')} - ${quantity} x ${formatCurrency(unitPrice)}${daysSuffix} = ${formatCurrency(quoteItemTotal(item))}`;
    })
    .join('<br>');

const quoteProductTable = payload => {
  const days = normalizeRentalDays(payload?.rental_days);
  const rows = quoteInvoiceItems(payload)
    .map(item => {
      const quantity = quoteItemQuantity(item);
      const unitPrice =
        Number(item.unit_price ?? item.unitPrice ?? 0) ||
        quoteItemTotal(item) / quantity;
      const daysCell =
        days > 1 ? `<td>${skipsRentalDays(item) ? '—' : days}</td>` : '';
      return `<tr><td>${escapeHtml(item.product_name || 'Item')}</td><td>${quantity}</td><td>${formatCurrency(unitPrice)}</td>${daysCell}<td><strong>${formatCurrency(quoteItemTotal(item))}</strong></td></tr>`;
    })
    .join('');

  if (!rows) return '';

  const daysHeader = days > 1 ? '<th>Diárias</th>' : '';
  return `<table><thead><tr><th>Produto(s)</th><th>Qtd</th><th>Valor/Período</th>${daysHeader}<th>Total</th></tr></thead><tbody>${rows}</tbody></table>`;
};

const quoteAddress = payload => payload?.recipient_address || {};

const quoteFullAddress = payload => {
  const address = quoteAddress(payload);
  return [
    [address.address, address.number].filter(Boolean).join(', '),
    address.complement,
    address.neighborhood,
    [address.city, address.state].filter(Boolean).join(' - '),
    address.zip_code,
  ]
    .filter(Boolean)
    .join(', ');
};

export const quoteTemplateValues = payload => ({
  // Orçamento de um contrato ainda não assinado: link para o cliente assinar.
  link_assinatura: payload.signature_url || '',
  numero: payload.quote_number || payload.contract_id || '-',
  numero_orcamento: payload.quote_number || payload.contract_id || '-',
  nome: payload.recipient_name || '-',
  cpf: payload.recipient_cpf || '-',
  cnpj:
    String(payload.recipient_cpf || '').replace(/\D/g, '').length === 14
      ? payload.recipient_cpf
      : '',
  documento: payload.recipient_cpf || '-',
  tipo_documento:
    String(payload.recipient_cpf || '').replace(/\D/g, '').length === 14
      ? 'CNPJ'
      : 'CPF',
  telefone: payload.recipient_phone || '-',
  forma_pagamento: payload.payment_method || '-',
  data: new Date().toLocaleDateString('pt-BR'),
  data_geracao: new Date().toLocaleDateString('pt-BR'),
  data_entrega: formatDateTime(payload.scheduled_at),
  data_retirada: formatDateTime(payload.pickup_at),
  produto: quoteProductSummary(payload),
  // Uma linha por produto, igual à mensagem padrão do orçamento.
  produtos: quoteProductList(payload),
  produtos_lista: quoteProductList(payload),
  produtos_tabela: quoteProductTable(payload),
  total: formatCurrency(quoteTotal(payload)),
  total_produtos: formatCurrency(quoteTotal(payload)),
  valor_total_produtos: formatCurrency(quoteTotal(payload)),
  diarias: String(normalizeRentalDays(payload.rental_days)),
  diarias_texto:
    normalizeRentalDays(payload.rental_days) === 1
      ? '1 diária'
      : `${normalizeRentalDays(payload.rental_days)} diárias`,
  cep: quoteAddress(payload).zip_code || '',
  endereco: quoteAddress(payload).address || '',
  numero_endereco: quoteAddress(payload).number || '',
  complemento: quoteAddress(payload).complement || '',
  bairro: quoteAddress(payload).neighborhood || '',
  cidade: quoteAddress(payload).city || '',
  uf: quoteAddress(payload).state || '',
  endereco_completo: quoteFullAddress(payload) || '-',
});
